import type { BusinessOverviewResponse } from '@pulsify/shared/dto'
import type { UseQueryResult } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useEvents } from '~/entities/event/model/use-events'
import { useActiveProfileId } from '~/entities/profile/model/use-profiles'
import { useBusinessOverview } from '~/entities/stats/model/use-business-overview'
import { CompanyDetailsHint } from '~/features/company-details/company-details-hint'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { MINUTES } from '~/shared/lib/time-constants'
import { useNow } from '~/shared/lib/use-now'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'
import {
  formatCount,
  type GreetingPeriod,
  greetingPeriod,
  relativeChange,
  todayLabel,
} from '../lib/overview-format'
import { runningEvents } from '../lib/running-events'
import { KpiTile } from './kpi-tile'
import { LiveNowBar } from './live-now-bar'
import { ReachChart } from './reach-chart'
import { TicketEarningsCard } from './ticket-earnings-card'
import { UpcomingEventsCard } from './upcoming-events-card'

type OverviewQuery = UseQueryResult<BusinessOverviewResponse>

const KPI_GRID = 'grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4'
const TILE_SKELETONS = ['followers', 'planning', 'here-now', 'views']
const MAX_LIVE_BARS = 3

function greeting(period: GreetingPeriod, name: string): string {
  if (period === 'morning') return m.overviewGreetingMorning({ name })
  if (period === 'afternoon') return m.overviewGreetingAfternoon({ name })
  return m.overviewGreetingEvening({ name })
}

export function OverviewPage() {
  const locale = getLocale()
  const now = useNow(MINUTES)
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const overview = useBusinessOverview(businessId)
  const upcoming = useEvents(businessId, 'upcoming', useActiveProfileId(businessId))
  const live = runningEvents(upcoming.events, now).slice(0, MAX_LIVE_BARS)
  const failed = !overview.data && overview.isError

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-1.5">
          {business ? (
            <h1 className="text-balance font-semibold text-2xl text-foreground leading-tight tracking-[-0.025em] sm:text-[30px]">
              {greeting(
                greetingPeriod(new Date(now).getHours()),
                business.companyName || business.legalName,
              )}
            </h1>
          ) : (
            <Skeleton className="h-8 w-72 max-w-full sm:h-9" />
          )}
          <p className="text-muted-foreground text-sm sm:text-base">{todayLabel(now, locale)}</p>
        </header>

        {live.length > 0 ? (
          <div className="flex flex-col gap-2">
            {live.map((event) => (
              <LiveNowBar key={event.id} event={event} locale={locale} />
            ))}
          </div>
        ) : null}

        <CompanyDetailsHint />
      </div>

      <KpiSection query={overview} locale={locale} />

      <div className="grid gap-6 xl:grid-cols-5 xl:items-start">
        <div
          className={cn(
            'flex min-w-0 flex-col gap-6 xl:col-span-2 xl:col-start-4 xl:row-start-1',
            failed && 'xl:col-span-5 xl:col-start-1',
          )}
        >
          <TicketEarningsCard locale={locale} now={now} />
          <UpcomingEventsCard
            businessId={businessId}
            locale={locale}
            hiddenIds={new Set(live.map((event) => event.id))}
          />
        </div>
        <ReachSection
          query={overview}
          locale={locale}
          className="xl:col-span-3 xl:col-start-1 xl:row-start-1"
        />
      </div>
    </div>
  )
}

function KpiSection({ query, locale }: { query: OverviewQuery; locale: string }) {
  if (query.data) return <KpiGrid figures={query.data} locale={locale} />

  if (query.isError) {
    return (
      <Card className="flex flex-col items-start gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-destructive text-sm">{m.statsLoadError()}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw className={cn(query.isFetching && 'motion-safe:animate-spin')} />
          {m.tryAgain()}
        </Button>
      </Card>
    )
  }

  return (
    <div className={KPI_GRID}>
      {TILE_SKELETONS.map((tile) => (
        <Skeleton key={tile} className="h-28 rounded-xl" />
      ))}
    </div>
  )
}

function KpiGrid({ figures, locale }: { figures: BusinessOverviewResponse; locale: string }) {
  const count = (value: number) => formatCount(value, locale)

  return (
    <div className={KPI_GRID}>
      <KpiTile label={m.overviewKpiFollowers()} value={count(figures.followers)} locale={locale} />
      <KpiTile
        label={m.overviewKpiPlanning()}
        value={count(figures.planningToVisit)}
        locale={locale}
      />
      <KpiTile label={m.overviewKpiHereNow()} value={count(figures.hereNow)} locale={locale} />
      <KpiTile
        label={m.overviewKpiViews()}
        value={count(figures.views.last30d)}
        change={relativeChange(figures.views.last30d, figures.views.previous30d)}
        changeLabel={m.overviewDeltaVsPrevious()}
        sparkline={figures.daily.map((day) => day.views)}
        locale={locale}
      />
    </div>
  )
}

function ReachSection({
  query,
  locale,
  className,
}: {
  query: OverviewQuery
  locale: string
  className?: string
}) {
  if (query.data) {
    const { impressions, daily } = query.data
    return (
      <ReachChart
        className={className}
        daily={daily}
        total={impressions.last30d}
        change={relativeChange(impressions.last30d, impressions.previous30d)}
        locale={locale}
      />
    )
  }
  if (query.isError) return null
  return <Skeleton className={cn('h-96 rounded-xl', className)} />
}
